/**
 * game-start-strip-flight.js — the reference strip's play-exit flight.
 *
 * When the player commits (Play), the strip's six tiles fly into the top-right menu button as
 * the start screen disassembles (menu-strip-play-exit-flight, navigation-discoverability): the
 * motion teaches "this is where these live from now on" — DESIGN Principle 5 by motion rather
 * than words, riding the one beat the etch-introduction rules leave open to choreography (the
 * exit is a real transition, SCREENS Decision 5). The button itself stays put: the start
 * screen's `.menu-button-start` is never faded by the exit, and the in-game `.menu-button`
 * mounts on the same engine vars, so the destination survives the board swap.
 *
 * Mirror of the tiles' own arrival gesture (animation/components/glass-condense.js): the TILES
 * group left the rows group's frame-etch for a gesture of its own on entrance, and here it
 * leaves the exit's shared fade (game-start-toggles-etch.js#EXIT_FADE_SELECTOR) the same way.
 *
 * Two modes, A/B'd live with Andre (prototype-first): `icons` — each tile's plate fades in
 * place while its icon detaches and streams into the button (the icon is the identity that
 * reappears inside the menu); `tiles` — the whole glass tile flies and shrinks into it.
 *
 * Compositor-only per docs/PERFORMANCE.md: transform + opacity only. Deliberately NOT
 * will-change-pinned: a burst-scoped inline pin shipped here reasoned, and the on-device A/B
 * (Pixel 5, 2026-08-31) measured it free in both directions — no frame-count deficit
 * unpinned, no cost pinned — so it came out rather than stand as unearned mechanism
 * (docs/PERFORMANCE.md § burst-scoped will-change carries the row; device-ab.js's `flightpin`
 * arm re-adds it for a re-measure before any reintroduction). Read-then-write: every rect is
 * measured before the first style write.
 *
 * Kit idiom: STRIP_FLIGHT_DEFAULTS knob bag (lab-tunable — the Strip Flight animation-lab
 * tab), addStripFlight merges caller overrides.
 */
import { gsap } from "../vendor/gsap.js";
import { STRIP_TILES_SELECTOR } from "./game-start-reference-strip.js";

export const STRIP_FLIGHT_DEFAULTS = {
  mode: "tiles", // tiles | icons — tiles won the live A/B (Andre, 2026-08-30); icons stays lab-selectable
  flightS: 0.7, // play-test: one flyer's travel into the button — slow enough to be noticed (Andre's pick, 2026-08-30)
  staggerS: 0.075, // play-test: gap between departures, in `order` (Andre's pick, 2026-08-30)
  order: "ltr", // ltr | rtl | all — departure order across the row (rtl = nearest the button first)
  arriveScale: 1.25, // play-test: the flyer's scale on arrival — it GROWS a little as it comes toward you, which is what explains its dissolve
  fadeAtFrac: 1, // play-test: where along the flight the dissolve starts (1 = on landing — never before it, Andre)
  fadeS: 0.2, // play-test: the dissolve's span once it starts — the tile settles under the button and its overhang melts
  labelFadeS: 0.12, // play-test (icons): the label dissolves before its icon leaves
  plateFadeS: 0.35, // play-test (icons): the emptied plate's in-place fade — the exit's own fade tempo
  plateSinkPx: 14, // play-test (icons): the plate's sink while fading — matches the exit's other fades
  pulseScale: 1.06, // play-test: the button's acknowledgment per arrival (1 = none)
  pulseS: 0.16, // play-test: one acknowledgment pulse, out and back
  ease: "power2.in", // accelerating into the button — drawn in, not thrown
};

function departureOrder(count, order) {
  const idx = [...Array(count).keys()];
  return order === "rtl" ? idx.reverse() : idx;
}

function center(rect) {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

// icons mode: lift the icon out of its tile into `host` at its exact on-screen box, so the
// plate can fade underneath without taking the icon with it. The `.gs-strip-tile > svg` sizing
// rule stops matching once reparented, so the measured box is written inline.
function liftIcon(icon, rect, hostRect) {
  gsap.set(icon, {
    position: "absolute",
    left: rect.left - hostRect.left,
    top: rect.top - hostRect.top,
    width: rect.width,
    height: rect.height,
    zIndex: 79, // just under the button (z 80): the icon slides in beneath the glass
    pointerEvents: "none",
  });
}

// Read phase, then write phase — every rect measured before the first style write. Returns
// the flyers (tile + the element that flies + its rest rect) and the button's centre.
function stageFlyers(screen, target, host, config) {
  const icons = config.mode === "icons";
  const targetCenter = center(target.getBoundingClientRect());
  const hostRect = host.getBoundingClientRect();
  const flyers = [...screen.querySelectorAll(STRIP_TILES_SELECTOR)].map(
    (tile) => {
      const flyer = icons ? tile.querySelector("svg") : tile;
      return { tile, flyer, rect: flyer.getBoundingClientRect() };
    },
  );
  if (icons) {
    for (const { flyer, rect } of flyers) {
      host.appendChild(flyer);
      liftIcon(flyer, rect, hostRect);
    }
  }
  gsap.set(
    flyers.map((f) => f.flyer),
    { transformOrigin: "center", zIndex: 79 },
  );
  return { flyers, targetCenter };
}

// icons mode: the emptied plate fades in place, its label dissolving first.
function fadePlateInPlace(master, tile, start, config) {
  master.to(
    tile.querySelector(".gs-strip-tile-label"),
    { autoAlpha: 0, duration: config.labelFadeS, ease: "power1.in" },
    start,
  );
  master.to(
    tile,
    {
      autoAlpha: 0,
      y: config.plateSinkPx,
      duration: config.plateFadeS,
      ease: "power2.in",
    },
    start,
  );
}

// One flyer: travels centre-to-centre into the button, growing toward arriveScale, and
// dissolves from fadeAtFrac of the way (1 = on landing, never before it).
function flyOne(master, flyer, from, targetCenter, start, config) {
  master.to(
    flyer,
    {
      x: targetCenter.x - from.x,
      y: targetCenter.y - from.y,
      scale: config.arriveScale,
      duration: config.flightS,
      ease: config.ease,
    },
    start,
  );
  master.to(
    flyer,
    { autoAlpha: 0, duration: config.fadeS, ease: "power1.in" },
    start + config.flightS * config.fadeAtFrac,
  );
}

// The button acknowledges an arrival with one small pulse, out and back.
function pulseTarget(master, target, arrival, config) {
  master.to(
    target,
    {
      scale: config.pulseScale,
      duration: config.pulseS / 2,
      yoyo: true,
      repeat: 1,
      ease: "power1.out",
      transformOrigin: "center",
    },
    arrival,
  );
}

// The lab's replay seam: icons back in their tiles, every flyer to factory, the target's
// pulse transform cleared — only the transform, since the target keeps its own inline
// placement (the lab replica positions itself inline; `all` wiped it into the flow).
function buildRestore(flyers, target, icons) {
  return () => {
    const labels = flyers.map((f) =>
      f.tile.querySelector(".gs-strip-tile-label"),
    );
    gsap.killTweensOf([
      ...flyers.map((f) => f.flyer),
      ...flyers.map((f) => f.tile),
      ...labels,
      target,
    ]);
    flyers.forEach(({ tile, flyer }, i) => {
      if (icons) tile.prepend(flyer);
      gsap.set([tile, flyer, labels[i]], { clearProps: "all" });
    });
    gsap.set(target, { clearProps: "transform" });
  };
}

/**
 * Weave the flight onto `master` from `at`: every `.gs-strip-tile` in `screen` flies (or
 * sends its icon) into `target`. `host` is the positioned ancestor lifted icons are parented
 * into (the start screen itself in production, the stage in the lab). Returns a residue
 * `{ restore }` — production never needs it (the exit ends in a render wipe), the lab calls
 * it before each replay.
 *
 * Precondition: the tiles are AT REST when this is called. Every flyer's travel is an
 * absolute `x`/`y` computed once, from rects measured here (stageFlyers), so weaving a second
 * flight over a running one would stage off already-translated boxes and land that batch
 * short. Both production callers satisfy it — the exit runs once per screen, off a Play
 * button that latches — and the lab calls `restore` before each replay.
 */
export function addStripFlight(
  master,
  screen,
  target,
  host,
  at,
  overrides = {},
) {
  const config = { ...STRIP_FLIGHT_DEFAULTS, ...overrides };
  const icons = config.mode === "icons";
  const { flyers, targetCenter } = stageFlyers(screen, target, host, config);
  let lastPulse = -Infinity;
  departureOrder(flyers.length, config.order).forEach((tileIndex, k) => {
    const { tile, flyer, rect } = flyers[tileIndex];
    const start = at + (config.order === "all" ? 0 : k * config.staggerS);
    if (icons) fadePlateInPlace(master, tile, start, config);
    flyOne(master, flyer, center(rect), targetCenter, start, config);
    // Arrivals inside a pulse's own span share it, so two tweens never fight over the
    // button's scale.
    const arrival = start + config.flightS;
    if (config.pulseScale !== 1 && arrival >= lastPulse + config.pulseS) {
      pulseTarget(master, target, arrival, config);
      lastPulse = arrival;
    }
  });
  return { restore: buildRestore(flyers, target, icons) };
}
